/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { useState, useRef, useEffect } from "react";
import cn from "classnames";
import styles from "./service-users-list.module.scss";
import { ZitadelServiceUser } from "@src/types/user-management";
import { IndicatorText } from "@src/components/atoms/indicator-text/indicator-text";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { useDispatch, useSelector } from "react-redux";
import {
  updateCurrentlySelectedServiceUserForEditUserDrawer,
  updateModalsCloseDrawerModal,
} from "@src/reducers/your-reducer";
import { useI18N } from "@src/language/language-context";
import { RootState } from "@src/reducers/store";
import questionMarkIcon from "@src/assets/svg/question-mark-full-gray-12.svg";
import { RolesDescriptionPopover } from "@src/components/molecules/roles-description-popover/roles-description-popover";

interface ServiceUsersListProps {
  serviceUsers: ZitadelServiceUser[];
}

const cssClassName = "service-users-list";

interface ServiceUsersListHeaderProps {
  columns: { key: string; label: string }[];
  columnWidths: { [key: string]: number };
  resizing: {
    handleKey: string;
    leftCol: string;
    rightCol: string;
    startX: number;
    startLeftWidth: number;
    startRightWidth: number;
  } | null;
  handleMouseDown: (
    e: React.MouseEvent<HTMLDivElement>,
    leftColKey: string,
    rightColKey: string,
  ) => void;
}

const ServiceUsersHeader = ({
  columns,
  columnWidths,
  resizing,
  handleMouseDown,
}: ServiceUsersListHeaderProps) => {
  const i18n = useI18N();

  const rolesDescriptions = useSelector(
    (state: RootState) => state.yourSlice.rolesDescriptions,
  );

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const texts = {
    rolesDescriptionPopoverAltText: i18n(
      "modules.usersList.rolesDescriptionPopoverAltText",
    ),
  };

  return (
    <thead>
      <tr>
        {columns.map((column, index) => (
          <th
            key={column.key}
            style={{ width: `${columnWidths[column.key]}%` }}
            className={styles[`${cssClassName}__header`]}
            onPointerLeave={() => {
              setIsPopoverOpen(false);
            }}
          >
            {index > 0 ? (
              <div
                className={`${
                  styles[`${cssClassName}__header-resize-handle`]
                } ${
                  resizing?.handleKey === column.key
                    ? styles[`${cssClassName}__header-resize-handle--dragging`]
                    : ""
                }`}
                onMouseDown={(e) =>
                  handleMouseDown(e, columns[index - 1].key, column.key)
                }
              />
            ) : (
              <div
                className={styles[`${cssClassName}__header-static-handle`]}
              />
            )}
            <div className={styles[`${cssClassName}__header-content`]}>
              {column.label}
              {Object.keys(rolesDescriptions).length && index === 2 && (
                <>
                  <img
                    src={questionMarkIcon}
                    alt={texts.rolesDescriptionPopoverAltText}
                    className={styles[`${cssClassName}__roles-icon`]}
                    onPointerEnter={() => {
                      setIsPopoverOpen(true);
                    }}
                  />

                  {isPopoverOpen && (
                    <RolesDescriptionPopover
                      rolesDescriptions={rolesDescriptions}
                    />
                  )}
                </>
              )}
            </div>
          </th>
        ))}
      </tr>
    </thead>
  );
};

export const ServiceUsersList = ({ serviceUsers }: ServiceUsersListProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const isAllowedToManageServiceUsers = authContext?.permissions.includes(
    "manage-service-api-users",
  );

  const texts = {
    nameLabel: i18n("modules.serviceUsersList.nameLabel"),
    usernameLabel: i18n("modules.serviceUsersList.usernameLabel"),
    rolesLabel: i18n("modules.serviceUsersList.rolesLabel"),
    statusLabel: i18n("modules.serviceUsersList.statusLabel"),
    expirationDateLabel: i18n("modules.serviceUsersList.expirationDateLabel"),
    createdAtLabel: i18n("modules.serviceUsersList.createdAtLabel"),
    updatedAtLabel: i18n("modules.serviceUsersList.updatedAtLabel"),
    statusTextActive: i18n("modules.serviceUsersList.statusTextActive"),
    statusTextInactive: i18n("modules.serviceUsersList.statusTextInactive"),
    statusTextInitial: i18n("modules.serviceUsersList.statusTextInitial"),
    noServiceUsersFoundMessage: i18n(
      "modules.serviceUsersList.noServiceUsersFoundMessage",
    ),
  };

  const [columnWidths, setColumnWidths] = useState({
    name: 15,
    username: 18,
    roles: 15,
    status: 7,
    expirationDate: 15,
    createdAt: 15,
    updatedAt: 15,
  });

  const [resizing, setResizing] = useState(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const columns = [
    { key: "name", label: texts.nameLabel },
    { key: "username", label: texts.usernameLabel },
    { key: "roles", label: texts.rolesLabel },
    { key: "status", label: texts.statusLabel },
    { key: "expirationDate", label: texts.expirationDateLabel },
    { key: "createdAt", label: texts.createdAtLabel },
    { key: "updatedAt", label: texts.updatedAtLabel },
  ];

  const handleMouseDown = (e, leftColKey, rightColKey) => {
    e.preventDefault();
    e.stopPropagation();

    setResizing({
      handleKey: rightColKey,
      leftCol: leftColKey,
      rightCol: rightColKey,
      startX: e.clientX,
      startLeftWidth: columnWidths[leftColKey],
      startRightWidth: columnWidths[rightColKey],
    });
  };

  const handleMouseMove = (e) => {
    if (!resizing || !tableRef.current) return;

    const deltaX = e.clientX - resizing.startX;
    const tableWidth = tableRef.current.offsetWidth;
    const deltaPercent = (deltaX / tableWidth) * 100;

    const newLeftWidth = resizing.startLeftWidth + deltaPercent;
    const newRightWidth = resizing.startRightWidth - deltaPercent;

    if (newLeftWidth >= 5 && newRightWidth >= 5) {
      setColumnWidths((prev) => ({
        ...prev,
        [resizing.leftCol]: newLeftWidth,
        [resizing.rightCol]: newRightWidth,
      }));
    }
  };

  const handleMouseUp = () => {
    setResizing(null);
  };

  const handleStatusIndicator = (status) => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "INACTIVE":
        return "orange";
      case "INITIAL":
        return "blue";
      default:
        return "blue";
    }
  };

  const handleStatusText = (status) => {
    switch (status) {
      case "ACTIVE":
        return texts.statusTextActive;
      case "INACTIVE":
        return texts.statusTextInactive;
      case "INITIAL":
        return texts.statusTextInitial;
      default:
        return "Unspecified";
    }
  };

  const renderCellContent = (columnKey: string, user: ZitadelServiceUser) => {
    switch (columnKey) {
      case "name":
        return user.name || "";
      case "username":
        return user.username || "";
      case "roles":
        return Array.isArray(user.roles) ? user.roles.join(", ") : "";
      case "status":
        return (
          <IndicatorText
            text={handleStatusText(user.status)}
            variant={handleStatusIndicator(user.status)}
          />
        );
      case "expirationDate":
        return <TimezoneDate date={user.expiration_date} />;
      case "createdAt":
        return <TimezoneDate date={user.created_at} />;
      case "updatedAt":
        return <TimezoneDate date={user.updated_at} />;
      default:
        return "";
    }
  };

  const handleClick = (user: ZitadelServiceUser) => {
    dispatch(
      updateModalsCloseDrawerModal({
        showModal: true,
        id: "editServiceUserDrawerModal",
      }),
    );
    dispatch(updateCurrentlySelectedServiceUserForEditUserDrawer(user));
  };

  useEffect(() => {
    if (resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [resizing]);

  return (
    <div className={styles[`${cssClassName}__container`]}>
      <div className={styles[`${cssClassName}__wrapper`]}>
        {serviceUsers.length === 0 ? (
          <div className={styles[`${cssClassName}__empty-wrapper`]}>
            <p className={styles[`${cssClassName}__empty-message`]}>
              {texts.noServiceUsersFoundMessage}
            </p>
          </div>
        ) : (
          <div className={styles[`${cssClassName}__scroll`]}>
            <table ref={tableRef} className={styles[`${cssClassName}__table`]}>
              <ServiceUsersHeader
                columns={columns}
                columnWidths={columnWidths}
                resizing={resizing}
                handleMouseDown={handleMouseDown}
              />
              <tbody>
                {serviceUsers.map((serviceUser) => (
                  <tr
                    key={serviceUser.id}
                    className={cn(styles[`${cssClassName}__row`], {
                      [styles[`${cssClassName}__row--clickable`]]:
                        isAllowedToManageServiceUsers,
                    })}
                    onClick={
                      isAllowedToManageServiceUsers
                        ? () => handleClick(serviceUser)
                        : undefined
                    }
                  >
                    {columns.map((column, index) => (
                      <td
                        key={column.key}
                        style={{ width: `${columnWidths[column.key]}%` }}
                        className={styles[`${cssClassName}__cell`]}
                      >
                        <div
                          className={styles[`${cssClassName}__cell-content`]}
                        >
                          {renderCellContent(column.key, serviceUser)}
                        </div>
                        {index < columns.length - 1 && (
                          <div
                            className={
                              styles[`${cssClassName}__cell-separator`]
                            }
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
